export const INSTAGRAM_CLIENT_ID = '481808398034661';
export const INSTAGRAM_SCOPES =
  'instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_content_publish';

export const SOCIAL_REDIRECT_PATH = '/SocialMediaRedirect';
export const OAUTH_COMPLETED_MESSAGE_TYPE = 'SOCIAL_OAUTH_COMPLETED';
export const OAUTH_RESULT_STORAGE_KEY = 'social_oauth_result';
export const INSTAGRAM_OAUTH_PENDING_KEY = 'instagram_oauth_pending';
export const INSTAGRAM_OAUTH_RESULT_KEY = 'instagram_oauth_result';
export const INSTAGRAM_OAUTH_POPUP_NAME = 'instagram_oauth_popup';

export const OAUTH_POLL_INTERVAL_MS = 500;
export const OAUTH_INSTAGRAM_SYNC_ATTEMPTS = 4;
export const OAUTH_INSTAGRAM_SYNC_DELAY_MS = 1500;
/** Grace period after popup closes before treating flow as failed */
export const OAUTH_INSTAGRAM_POPUP_CLOSED_GRACE_MS = 1500;
/** Max wait for callback when popup stays open (e.g. redirected to Instagram feed) */
export const OAUTH_INSTAGRAM_CALLBACK_TIMEOUT_MS = 120000;
/** Max age for a pending OAuth session or return result */
export const OAUTH_INSTAGRAM_SESSION_MAX_AGE_MS = 15 * 60 * 1000;
/** Max automatic retries when OAuth does not reach SocialMediaRedirect */
export const OAUTH_INSTAGRAM_AUTH_RETRY_ATTEMPTS = 1;
/** Short pause before forcing the auth URL again */
export const OAUTH_INSTAGRAM_AUTH_RETRY_DELAY_MS = 600;
/** Must stay in sync with SocialMediaRedirect popup close delay */
export const INSTAGRAM_POPUP_CLOSE_DELAY_MS = 2000;

/** Pages where OAuth runs in a popup and the parent must stay on screen (no redirect). */
export const INSTAGRAM_POPUP_ONLY_RETURN_PAGES = [
  'ChatSocketPage',
  'CampaignChatPage',
];

export const resolveInstagramOAuthReturnPage = (pathname = '') => {
  const normalizedPath = pathname.toLowerCase();

  if (normalizedPath === '/' || normalizedPath.includes('/chatsocket')) {
    return 'ChatSocketPage';
  }

  if (
    normalizedPath.includes('/campaign/chat') ||
    normalizedPath.includes('/agent/chat')
  ) {
    return 'CampaignChatPage';
  }

  if (normalizedPath.includes('/myaccountpage')) {
    return 'MyAccountPage';
  }

  if (normalizedPath.includes('/stepspage')) {
    return 'StepsPage';
  }

  return 'MyAccountPage';
};

export const resolveInstagramOAuthReturnPath = (returnPage) => {
  switch (returnPage) {
    case 'ChatSocketPage':
      return '/ChatSocket';
    case 'CampaignChatPage':
      return '/campaign/chat';
    case 'MyAccountPage':
      return '/MyAccountPage';
    case 'StepsPage':
      return '/StepsPage';
    default:
      return '/ChatSocket';
  }
};

export const buildInstagramOAuthUrl = (businessId, returnPage) => {
  const redirectUri = `${window.location.origin}${SOCIAL_REDIRECT_PATH}`;
  const stateValue = `instagram,${returnPage},${businessId}`;
  const params = new URLSearchParams({
    client_id: INSTAGRAM_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: INSTAGRAM_SCOPES,
    state: stateValue,
  });

  if (returnPage === 'ChatSocketPage') {
    params.set('force_reauth', 'false');
    params.set('enable_fb_login', 'false');
  }

  // #weblink keeps OAuth in the browser through login, captcha and consent
  // (prevents native app from hijacking the flow on mobile).
  return `https://www.instagram.com/oauth/authorize?${params.toString()}#weblink`;
};

export const markInstagramOAuthPending = (
  businessId,
  returnPage,
  returnPath,
) => {
  try {
    sessionStorage.setItem(
      INSTAGRAM_OAUTH_PENDING_KEY,
      JSON.stringify({
        businessId,
        returnPage,
        returnPath:
          returnPath || resolveInstagramOAuthReturnPath(returnPage),
        ts: Date.now(),
      }),
    );
  } catch {
    // noop
  }
};

export const readInstagramOAuthPending = () => {
  try {
    const raw = sessionStorage.getItem(INSTAGRAM_OAUTH_PENDING_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (Date.now() - parsed?.ts > OAUTH_INSTAGRAM_SESSION_MAX_AGE_MS) {
      clearInstagramOAuthPending();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const clearInstagramOAuthPending = () => {
  try {
    sessionStorage.removeItem(INSTAGRAM_OAUTH_PENDING_KEY);
  } catch {
    // noop
  }
};

export const writeInstagramOAuthReturnResult = ({
  success = false,
  returnPath,
  businessId,
} = {}) => {
  try {
    sessionStorage.setItem(
      INSTAGRAM_OAUTH_RESULT_KEY,
      JSON.stringify({
        success: !!success,
        returnPath,
        businessId,
        ts: Date.now(),
      }),
    );
  } catch {
    // noop
  }
};

export const peekInstagramOAuthReturnResult = () => {
  try {
    const raw = sessionStorage.getItem(INSTAGRAM_OAUTH_RESULT_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (Date.now() - parsed?.ts > OAUTH_INSTAGRAM_SESSION_MAX_AGE_MS) {
      clearInstagramOAuthReturnResult();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const consumeInstagramOAuthReturnResult = () => {
  const result = peekInstagramOAuthReturnResult();
  clearInstagramOAuthReturnResult();
  return result;
};

export const clearInstagramOAuthReturnResult = () => {
  try {
    sessionStorage.removeItem(INSTAGRAM_OAUTH_RESULT_KEY);
  } catch {
    // noop
  }
};

export const getCurrentReturnPath = () =>
  `${window.location.pathname}${window.location.search}`;

export const isInstagramPopupOnlyReturnPage = (returnPage) =>
  INSTAGRAM_POPUP_ONLY_RETURN_PAGES.includes(returnPage);

export const clearStoredOAuthResult = () => {
  try {
    localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
  } catch {
    // noop
  }
};

export const readStoredOAuthResult = (providerKey = 'instagram') => {
  try {
    const raw = localStorage.getItem(OAUTH_RESULT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (
      parsed?.provider !== providerKey ||
      parsed?.success !== true ||
      Date.now() - parsed?.ts > 60000
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const chatSocketOAuthPathsMatch = (expectedPath, currentPath) => {
  if (!expectedPath) {
    return true;
  }

  return (
    normalizeChatSocketOAuthPath(expectedPath) ===
    normalizeChatSocketOAuthPath(currentPath)
  );
};

/** Mobile and narrow touch devices use same-tab redirect (popup is often blocked). */
export const shouldUseInstagramOAuthRedirect = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const isCoarsePointer =
    window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const isNarrowViewport =
    window.matchMedia?.('(max-width: 768px)').matches ?? false;

  return isMobileUserAgent || (isCoarsePointer && isNarrowViewport);
};

export const getChatSocketOAuthReturnPath = (search = '') => {
  const query =
    search || (typeof window !== 'undefined' ? window.location.search : '');
  return `/ChatSocket${query || ''}`;
};

export const normalizeChatSocketOAuthPath = (path = '') => {
  const queryIndex = path.indexOf('?');
  const pathname = queryIndex >= 0 ? path.slice(0, queryIndex) : path;
  const search = queryIndex >= 0 ? path.slice(queryIndex) : '';
  const normalizedPathname =
    pathname === '/' || pathname.toLowerCase() === '/chatsocket'
      ? '/ChatSocket'
      : pathname;

  return `${normalizedPathname}${search}`;
};

export const getInstagramOAuthPopupFeatures = () => 'width=500,height=600';

export const openInstagramOAuthPopup = (authUrl) => {
  const popup = window.open(
    authUrl,
    INSTAGRAM_OAUTH_POPUP_NAME,
    getInstagramOAuthPopupFeatures(),
  );

  if (popup) {
    try {
      popup.focus();
    } catch {
      // noop
    }
  }

  return popup;
};

/** Re-navigate an existing popup or open a new one with the Instagram OAuth URL. */
export const relaunchInstagramOAuthPopup = (popup, authUrl) => {
  if (popup && !popup.closed) {
    try {
      popup.location.href = authUrl;
      popup.focus();
      return popup;
    } catch {
      try {
        popup.close();
      } catch {
        // noop
      }
    }
  }

  return openInstagramOAuthPopup(authUrl);
};
